/**
 * NaamDarbar — AppIcon (web)
 *
 * Renders the project's custom SVG icon set (src/assets/icons/*.svg) as
 * tintable React components via vite-plugin-svgr (`?react`). The source SVGs
 * have no per-path fill, so `color` cascades (currentColor) and `fill` tints
 * the whole glyph to match the surrounding UI.
 *
 * Usage: <AppIcon name="diamond-turn-right" size={20} className="text-kesri-600" />
 *        <AppIcon name="piano" size={16} color="#f59e0b" />
 */

import DiamondTurnRight from '../../assets/icons/diamond-turn-right.svg?react';
import CalendarStar from '../../assets/icons/calendar-star.svg?react';
import Camping from '../../assets/icons/camping.svg?react';
import Piano from '../../assets/icons/piano-keyboard.svg?react';
import TourGuide from '../../assets/icons/tour-guide-people.svg?react';
import MortarPestle from '../../assets/icons/mortar-pestle.svg?react';
import GovernmentFlag from '../../assets/icons/government-flag.svg?react';
import Home from '../../assets/icons/home.svg?react';
import Medal from '../../assets/icons/medal.svg?react';

export type AppIconName =
  | 'diamond-turn-right'
  | 'calendar-star'
  | 'camping'
  | 'piano'
  | 'tour-guide'
  | 'mortar-pestle'
  | 'government-flag'
  | 'home'
  | 'medal';

const ICONS: Record<AppIconName, React.FC<React.SVGProps<SVGSVGElement>>> = {
  'diamond-turn-right': DiamondTurnRight,
  'calendar-star': CalendarStar,
  camping: Camping,
  piano: Piano,
  'tour-guide': TourGuide,
  'mortar-pestle': MortarPestle,
  'government-flag': GovernmentFlag,
  home: Home,
  medal: Medal,
};

interface AppIconProps {
  name: AppIconName;
  size?: number;
  /** Explicit color; omit to inherit `currentColor` from a text-* class. */
  color?: string;
  className?: string;
}

export default function AppIcon({ name, size = 24, color = 'currentColor', className }: AppIconProps) {
  const Svg = ICONS[name];
  // No per-path fill in the source, so the root fill cascades to every path.
  return <Svg width={size} height={size} fill={color} color={color} className={className} />;
}
